import { isEmpty as _isEmpty } from 'lodash';
import { useMutation as useMrMutation } from '../index';
import permissions from '../Apollo/permissions';
import { buildVariables, mutation } from './control-proxy-mutation-builder';
import { METHOD, PATH, PIPELINE_SCOPE } from './control-api';
import mergeQueries from '../Apollo/merge-queries';
import { useUserContext } from '@ion/user';
import { isValidJSONObject } from '@ion/mrdash';

export function useControlProxy({ onCompleted, additionalQueries = [], ...options }) {
  const combinedMutation = combineMutations(additionalQueries);

  const [executeMutation, response] = useMrMutation(combinedMutation, {
    onCompleted: r => {
      if (onCompleted && r.control_proxy?.data) onCompleted(JSON.parse(r.control_proxy.data));
    },
    context: { permission: options.permission || permissions.deployPipeline },
    ...options,
  });

  const callControlProxy = ({ additionalVariables = {}, ...args }) => {
    const { clusterId, writekey, method, path, body } = args;
    return executeMutation({
      variables: {
        ...buildVariables({ clusterId, writekey, method, path, body }),
        ...additionalVariables,
      },
    }).then(response => {
      // TODO: I'm not sure if we should be hooking into the promise like this
      // because there is already an `onCompleted` function and a `response` object.
      return handleResponse(response);
    });
  };

  return [callControlProxy, response];
}

function combineMutations(additionalQueries) {
  if (!_isEmpty(additionalQueries)) {
    return mergeQueries('useControlProxyCombined', [mutation, ...additionalQueries]);
  }
  return mutation;
}

function handleResponse(response) {
  const data = extractData(response);
  return data ? data : response;
}

function extractData(response) {
  const data = response.data?.control_proxy?.data;
  if (isValidJSONObject(data)) {
    return JSON.parse(data);
  }
  return data;
}

const useGetSchemaSummary = (options = {}) => {
  const { user, currentOrg } = useUserContext();
  const userData = { email: user?.email, org: currentOrg?.display_name };
  const [call, response] = useControlProxy({ permission: permissions.readAnalytics, ...options });
  return [
    ({ clusterId, earliestSeen, timeframe }) => {
      track({ userData, clusterId, timeframe });
      return call({
        path: PATH.GeneratedEventSchemasSummary,
        method: METHOD.GET,
        body: { earliestSeen },
        clusterId,
      });
    },
    response,
  ];

  function track({ userData, clusterId, timeframe }) {
    __mrTracking?.track('schema_report_generated', {
      userEmail: userData.email,
      orgName: userData.org,
      clusterId: clusterId,
      timeframe: timeframe,
    });
  }
};

const useGetGeneratedSchemas = (options = {}) => {
  const [call, response] = useControlProxy({ permission: permissions.readAnalytics, ...options });
  return [
    ({ clusterId, body }) => {
      return call({
        path: PATH.GeneratedEventSchemas,
        method: METHOD.GET,
        body,
        clusterId,
      });
    },
    response,
  ];
};

const useGetEventSchemaDetails = (options = {}) => {
  const [call, response] = useControlProxy({ permission: permissions.readAnalytics, ...options });
  return [
    ({ clusterId, eventName, writekeys, earliestSeen, body }) => {
      return call({
        path: PATH.GeneratedEventSchemasDetails,
        method: METHOD.GET,
        body: { eventName, writekeys, earliestSeen, ...body },
        clusterId,
      });
    },
    response,
  ];
};

const useDiffPipelines = (options = {}) => {
  const [call, response] = useControlProxy({ permission: permissions.readPipeline, ...options });
  return [
    ({ clusterId, writekey, proposedJSON }) => {
      return call({
        path: PATH.PipelineDiff,
        method: METHOD.POST,
        body: { proposed: proposedJSON, scope: PIPELINE_SCOPE },
        clusterId,
        writekey,
      });
    },
    normalizeResponse(response),
  ];
};

const useDeletePipeline = (options = {}) => {
  const [call, response] = useControlProxy({ permission: permissions.deletePipeline, ...options });
  return [
    ({ clusterId, writekey }) => {
      return call({
        path: PATH.Pipeline,
        method: METHOD.DELETE,
        body: { scope: PIPELINE_SCOPE, writeKey: writekey },
        clusterId,
        writekey,
      });
    },
    response,
  ];
};

const useGetPipeline = (options = {}) => {
  const { transformNotDeployed, ...requestOptions } = options;

  const [call, response] = useControlProxy({ permission: permissions.readPipeline, ...requestOptions });

  const refetch = ({ clusterId, writekey } = {}) => {
    return call({
      path: PATH.Pipeline,
      method: METHOD.GET,
      body: { scopes: PIPELINE_SCOPE, writeKey: writekey },
      clusterId,
      writekey,
    });
  };

  return [
    async ({ clusterId, writekey }) => {
      const controlResponse = await call({
        path: PATH.Pipeline,
        method: METHOD.GET,
        body: { scopes: PIPELINE_SCOPE, writeKey: writekey },
        clusterId,
        writekey,
      });

      return controlResponse;
    },
    { ...transformPipelineResponse(response, transformNotDeployed), refetch },
  ];
};

const useValidateIntegration = (options = {}) => {
  const [call, response] = useControlProxy({ permission: options.permission, ...options });
  return [
    ({ clusterId, integration }) => {
      return call({
        path: PATH.ValidateIntegration,
        method: METHOD.POST,
        body: { integration },
        clusterId,
      });
    },
    response,
  ];
};

const useCreateAJSFile = (options = {}) => {
  const [call, response] = useControlProxy({ permission: options.permission, ...options });
  return [
    ({ clusterId, writekey, gzippedFileContent, email }) => {
      return call({
        path: PATH.CreateAJSFile,
        method: METHOD.POST,
        body: { pipelineId: writekey, file: { gzippedFileContent: gzippedFileContent, author: email } },
        clusterId,
      });
    },
    response,
  ];
};

const useGetPipelineSchemaViolations = (options = {}) => {
  const [call, response] = useControlProxy({
    permission: permissions.readSchemas,
    ...options,
  });

  const getPipelineSchemaViolations = ({ clusterId, writekey, earliestSeen }) => {
    return call({
      path: PATH.PipelineSchemaViolations,
      method: METHOD.GET,
      body: { writeKey: writekey, earliestSeen },
      clusterId,
    });
  };

  return [getPipelineSchemaViolations, normalizeResponse(response, transformViolationsCount)];
};

const useGetPipelineSchemaViolationsDetails = (options = {}) => {
  const [call, response] = useControlProxy({
    permission: permissions.readSchemas,
    ...options,
  });

  const getPipelineSchemaViolationsDetails = ({ clusterId, writekey, schemaId, earliestSeen, eventName }) => {
    return call({
      path: PATH.PipelineSchemaViolationsDetails,
      method: METHOD.GET,
      body: { writeKey: writekey, schemaId, earliestSeen, eventName },
      clusterId,
    });
  };

  return [getPipelineSchemaViolationsDetails, normalizeResponse(response, transformViolationsCount)];
};

const useGetSecretCollection = (options = {}) => {
  const [call, response] = useControlProxy({ permission: permissions.readSecret, ...options });

  const getSecretCollection = ({ clusterId, collectionId }) => {
    return call({
      path: PATH.Secret,
      method: METHOD.GET,
      body: { id: collectionId },
      clusterId,
    });
  };

  return [getSecretCollection, normalizeResponse(response)];
};

const useCreateSecretCollection = (options = {}) => {
  const [call, response] = useControlProxy({ permission: permissions.createSecret, ...options });

  const createSecretCollection = ({ clusterId, collectionId, author, description, values }) => {
    return call({
      path: PATH.Secret,
      method: METHOD.POST,
      body: { id: collectionId, author, description, values },
      clusterId,
    });
  };

  return [createSecretCollection, response];
};

const useCloneSecretCollection = (options = {}) => {
  const [call, response] = useControlProxy({ permission: permissions.createSecret, ...options });

  const cloneSecretCollection = ({
    clusterId,
    collectionId,
    cloneFromId,
    author,
    description,
    values,
    keysToRemove,
  }) => {
    const updatedSecretCollection = {
      id: collectionId,
      author,
      description,
      values,
    };

    return call({
      path: PATH.SecretClone,
      method: METHOD.POST,
      body: { cloneFrom: cloneFromId, secret: updatedSecretCollection, removeValues: keysToRemove },
      clusterId,
    });
  };

  return [cloneSecretCollection, response];
};

function transformViolationsCount(data) {
  if (!data?.violations || !Array.isArray(data.violations)) {
    return data ?? [];
  }

  return data.violations.map(violation => ({
    ...violation,
    count: Number(violation.count),
  }));
}

/**
 * Expects a full response, rather than just the response data returned by normalizeResponse
 */
function transformPipelineResponse(response, transformNotDeployed) {
  const shouldTransformNotDeployed = transformNotDeployed && response.error?.errorMsg.includes('404');
  if (shouldTransformNotDeployed) {
    const emptyPipelineData = { pipeline: null };
    return { ...response, data: emptyPipelineData, error: undefined };
  }

  return normalizeResponse(response);
}

export function normalizeResponse(response, transformFn) {
  if (!response) {
    return response;
  }

  const normalizedData = extractData(response);

  const transformedData = transformFn ? transformFn(normalizedData) : normalizedData;

  return {
    ...response,
    data: transformedData,
  };
}

export {
  useDeletePipeline,
  useDiffPipelines,
  useGetEventSchemaDetails,
  useGetSchemaSummary,
  useGetGeneratedSchemas,
  useGetPipeline,
  useGetPipelineSchemaViolations,
  useGetPipelineSchemaViolationsDetails,
  useValidateIntegration,
  useCreateAJSFile,
  useGetSecretCollection,
  useCreateSecretCollection,
  useCloneSecretCollection,
};
